// =============================================================================
// FILE: packages/renderer/src/pages/CapabilitiesPage.tsx
// PURPOSE: "What R2 does for you" — a dedicated capabilities landing page opened
//          by clicking the R2 BIM OPS logo. It communicates the value of the
//          RCD² Engine per user level (Beginner → RCDD) so users instantly grasp
//          the concept. Matches the command-center aesthetic: dark glassmorphism,
//          teal/gold accents, staggered entrance animation.
// =============================================================================

import { useEffect, useState, type JSX } from 'react';
import {
  Sparkles, GraduationCap, Wrench, Rocket, Crown, ShieldCheck, ArrowRight, Check,
} from 'lucide-react';
import type { Route } from '../App.js';
import { RCD2EngineLockup } from '../components/branding/LogoMark.js';

interface Level {
  id: string;
  name: string;
  icon: typeof Sparkles;
  tagline: string;
  who: string;
  features: string[];
  cta: string;
  ctaRoute: Route;
  accent: string;   // rgb triplet
  featured?: boolean;
}

const LEVELS: Level[] = [
  {
    id: 'beginner',
    name: 'Beginner',
    icon: GraduationCap,
    tagline: 'Learn the ropes, guided every step',
    who: 'New to BIM & structured cabling — students, apprentices, career-changers.',
    features: [
      'Emmy AI coach explains every term in plain English',
      'Guided project setup with smart defaults',
      'Auto-generated cable schedules & takeoffs',
      'Instant "what does this mean?" answers',
    ],
    cta: 'Start free',
    ctaRoute: 'pricing',
    accent: '94,234,212',
  },
  {
    id: 'intermediate',
    name: 'Intermediate',
    icon: Wrench,
    tagline: 'Ship real projects, faster',
    who: 'Technicians & junior designers running day-to-day project work.',
    features: [
      'Full Estimator with labor & material pricing',
      'Rack & IDF drafting with drag-and-drop',
      'Clash detection on Revit / AutoCAD models',
      'One-click client-ready reports',
    ],
    cta: 'See plans',
    ctaRoute: 'pricing',
    accent: '99,179,237',
  },
  {
    id: 'advanced',
    name: 'Advanced',
    icon: Rocket,
    tagline: 'Automate the busywork',
    who: 'Senior designers & PMs managing multiple concurrent engagements.',
    features: [
      'Autopilot: AI drafts submittals & follow-ups',
      'Smart Ingestion — drop a spec, get a populated project',
      'Perfect Pathway routing & 90 m rule alerts',
      'Multi-project command dashboard',
    ],
    cta: 'Explore pricing',
    ctaRoute: 'pricing',
    accent: '167,139,250',
  },
  {
    id: 'expert',
    name: 'Expert',
    icon: Crown,
    tagline: 'Run a firm-grade operation',
    who: 'Lead engineers & firm owners standardizing delivery at scale.',
    features: [
      'MasterFormat specification engine',
      'CAD layering automation & .dxf exports',
      'Certification Pass — auto-polished, audit-ready packages',
      'Team standards, templates & cost libraries',
    ],
    cta: 'Talk to us',
    ctaRoute: 'pricing',
    accent: '252,211,77',
  },
  {
    id: 'rcdd',
    name: 'RCDD',
    icon: ShieldCheck,
    tagline: 'The certified professional standard',
    who: 'BICSI RCDD-credentialed designers who set the bar.',
    features: [
      'RCDD-verified badge & credential card on every deliverable',
      'Design intent validated against TIA/BICSI standards',
      'Priority Emmy models tuned for code compliance',
      'Legacy License & Founder-tier recognition',
    ],
    cta: 'Get certified access',
    ctaRoute: 'certification-pass',
    accent: '94,234,212',
    featured: true,
  },
];

export function CapabilitiesPage({ onNavigate }: { onNavigate: (r: Route) => void }): JSX.Element {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 40); return () => clearTimeout(t); }, []);

  return (
    <div data-testid="capabilities-page" className="relative mx-auto max-w-6xl px-6 py-10">
      <style>{`
        @keyframes cap-rise { from { opacity:0; transform: translateY(22px); } to { opacity:1; transform:none; } }
      `}</style>

      {/* Hero */}
      <div
        style={{ opacity: mounted ? 1 : 0, animation: mounted ? 'cap-rise 0.6s ease both' : undefined }}
        className="text-center flex flex-col items-center gap-5 mb-14"
      >
        <RCD2EngineLockup height={54} />
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full"
          style={{ background: 'rgba(45,212,191,0.10)', border: '1px solid rgba(45,212,191,0.3)' }}>
          <Sparkles className="h-3.5 w-3.5 text-teal-300" />
          <span className="text-[10px] uppercase tracking-[0.24em] text-teal-300">What R2 does for you</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ color: 'rgb(var(--fg))' }}>
          One engine. <span style={{ color: '#5eead4' }}>Every level</span> of your career.
        </h1>
        <p className="max-w-2xl text-base" style={{ color: 'rgb(var(--muted))' }}>
          The RCD² Engine meets you exactly where you are — from your first cable schedule to
          RCDD-certified, firm-grade delivery. Pick the level that sounds like you and see what unlocks.
        </p>
      </div>

      {/* Level cards */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {LEVELS.map((lvl, i) => {
          const Icon = lvl.icon;
          return (
            <div
              key={lvl.id}
              data-testid={`capability-card-${lvl.id}`}
              className="relative flex flex-col rounded-2xl p-6 transition-all hover:-translate-y-1"
              style={{
                background: 'rgba(9,17,28,0.72)',
                backdropFilter: 'blur(14px)',
                border: `1px solid rgba(${lvl.accent},${lvl.featured ? 0.55 : 0.22})`,
                boxShadow: lvl.featured
                  ? `0 0 30px rgba(${lvl.accent},0.16), 0 12px 40px rgba(0,0,0,0.45)`
                  : '0 8px 30px rgba(0,0,0,0.4)',
                opacity: mounted ? 1 : 0,
                animation: mounted ? `cap-rise 0.55s ease both` : undefined,
                animationDelay: `${0.08 * i + 0.1}s`,
              }}
            >
              {lvl.featured && (
                <span className="absolute -top-2.5 right-5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.18em]"
                  style={{ background: '#5eead4', color: '#04110f', boxShadow: '0 0 12px rgba(94,234,212,0.6)' }}>
                  Certified
                </span>
              )}
              <div className="flex items-center gap-3 mb-3">
                <div className="h-11 w-11 rounded-xl grid place-items-center"
                  style={{ background: `rgba(${lvl.accent},0.12)`, border: `1px solid rgba(${lvl.accent},0.35)` }}>
                  <Icon className="h-5 w-5" strokeWidth={1.9} style={{ color: `rgb(${lvl.accent})` }} />
                </div>
                <div>
                  <div className="text-lg font-bold" style={{ color: 'rgb(var(--fg))' }}>{lvl.name}</div>
                  <div className="text-[11px]" style={{ color: `rgb(${lvl.accent})` }}>{lvl.tagline}</div>
                </div>
              </div>
              <p className="text-[12.5px] mb-4" style={{ color: 'rgb(var(--muted))' }}>{lvl.who}</p>
              <ul className="flex flex-col gap-2 mb-5 flex-1">
                {lvl.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[12.5px]" style={{ color: 'rgba(226,236,240,0.88)' }}>
                    <Check className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" strokeWidth={2.4} style={{ color: `rgb(${lvl.accent})` }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                data-testid={`capability-cta-${lvl.id}`}
                onClick={() => onNavigate(lvl.ctaRoute)}
                className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.12em] transition-all"
                style={{
                  background: `rgba(${lvl.accent},0.14)`,
                  border: `1px solid rgba(${lvl.accent},0.5)`,
                  color: `rgb(${lvl.accent})`,
                }}
              >
                {lvl.cta} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer CTA */}
      <div className="mt-14 text-center" style={{ opacity: mounted ? 1 : 0, animation: mounted ? 'cap-rise 0.6s ease both' : undefined, animationDelay: '0.6s' }}>
        <p className="text-sm mb-4" style={{ color: 'rgb(var(--muted))' }}>Not sure where you fit? Ask Emmy — she'll point you to the right level.</p>
        <button
          type="button"
          data-testid="capabilities-ask-emmy"
          onClick={() => onNavigate('ai')}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[13px] font-semibold"
          style={{ background: 'linear-gradient(135deg, rgba(13,90,80,0.95), rgba(5,55,50,0.95))', border: '1px solid rgba(45,212,191,0.55)', color: '#5eead4', boxShadow: '0 0 22px rgba(45,212,191,0.2)' }}
        >
          <Sparkles className="h-4 w-4" /> Ask Emmy which level fits me
        </button>
      </div>
    </div>
  );
}
